import { useEffect } from 'react';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider } from 'react-router-dom';
import { router } from '@/app/router';
import { AuthProvider } from '@/auth/AuthProvider';
import { useAuth } from '@/auth/useAuth';
import { connectRealtime, disconnectRealtime, realtimeEventLabel } from '@/lib/realtime';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

/**
 * Bridges the AuthProvider's active client token into the realtime socket.
 *
 * Mounts a single socket.io connection to wss://ws.rsv.mx for the lifetime of
 * the app. The `identify` payload is re-emitted on every reconnect by
 * connectRealtime() itself, picking up whichever account is currently
 * activated via `selectedAccount.token` (multi-account staff). On token
 * change we tear the socket down so identify rebinds to the new client room.
 *
 * Polling-driven react-query caches stay authoritative; events are nudges
 * that invalidate the relevant queries so the UI catches up faster than the
 * 30s staleTime would allow.
 */
function RealtimeBridge() {
  const { selectedAccount, userToken, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const activeToken = selectedAccount?.token ?? userToken ?? null;

  useEffect(() => {
    if (!isAuthenticated) return;
    try {
      connectRealtime({
        appType: 'tickets',
        getAuthToken: () => activeToken,
        station: null,
        onConnect: (sid) => console.log('[realtime] tickets sid:', sid),
        onEvent: (event, data) => {
          console.log('[realtime] event', event, realtimeEventLabel(event), data);
          // Refresh any list-style query on broadcast events. The two
          // namespaces in use today are 'tickets' and 'orders' — invalidate
          // both rather than enumerate every concrete key.
          void queryClient.invalidateQueries({ queryKey: ['tickets'] });
          void queryClient.invalidateQueries({ queryKey: ['orders'] });
        },
      });
    } catch (err) {
      console.warn('[realtime] failed to initialize:', err);
    }
    return () => {
      disconnectRealtime();
    };
  }, [isAuthenticated, activeToken, queryClient]);

  return null;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RealtimeBridge />
        <RouterProvider router={router} />
      </AuthProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
